import React, { useEffect, useMemo, useState } from 'react';
import './Documents.css';
import api, { userService } from '../services/api';

const Documents = ({ user }) => {
  const canManageDocuments = user.role === 'admin' || user.role === 'supervisor';
  const displayDocName = (name, code) => (code === 'preukaz' || (name && name.toLowerCase().trim() === 'preukaz')) ? 'BSN' : name;
  const normalizeDownloadUrl = (url) => url ? url.replace(/(^https?:\/\/[^/]+)?\/(api\/)?uploads\//, '/uploads/') : url;
  const getStatusLabel = (status) => ({
    ok: 'Platný',
    expired: 'Po splatnosti',
    expiring: 'Končí čoskoro',
    missing: 'Chýba',
    optional_missing: 'Voliteľný – chýba'
  }[status] || status);

  const onDownload = async (url, filename) => {
    try {
      const finalUrl = normalizeDownloadUrl(url);
      const res = await fetch(finalUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      const obj = URL.createObjectURL(blob);
      a.href = obj;
      a.download = filename || 'dokument';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 500);
    } catch (e) {
      alert('Chyba pri sťahovaní: ' + (e.message || 'Neznáma chyba'));
    }
  };

  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(canManageDocuments);
  const [error, setError] = useState(null);
  const [myDocuments, setMyDocuments] = useState([]);
  const [myLoading, setMyLoading] = useState(!canManageDocuments);
  const [myError, setMyError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [uploadDrafts, setUploadDrafts] = useState({});
  const [uploadingCode, setUploadingCode] = useState(null);

  const loadMyDocuments = async () => {
    try {
      setMyLoading(true);
      setMyError(null);
      const { data } = await api.get(`/users/${user.id}/documents`);
      setMyDocuments(data.documents || []);
    } catch (e) {
      setMyError(e.response?.data?.error || e.message || 'Chyba pri načítaní dokumentov');
    } finally {
      setMyLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageDocuments) return;

    (async () => {
      try {
        setLoading(true);
        let list = [];
        if (user.role === 'admin') {
          list = await userService.getAll();
        } else if (user.role === 'supervisor' && user.project_id) {
          const { data } = await api.get(`/projects/${user.project_id}/users`);
          list = data;
        }
        const filtered = (user.role === 'admin') ? list : list.filter(u => u.project_id === user.project_id);
        const sorted = [...filtered].sort((a, b) => {
          if (!!a.blocked !== !!b.blocked) return a.blocked ? 1 : -1;
          const an = (a.full_name || a.username || '').toLowerCase();
          const bn = (b.full_name || b.username || '').toLowerCase();
          return an.localeCompare(bn, 'sk');
        });
        setUsers(sorted);
      } catch (e) {
        setError(e.message || 'Chyba pri načítaní používateľov');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, canManageDocuments]);

  useEffect(() => {
    if (canManageDocuments) return;
    loadMyDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, canManageDocuments]);

  const toggleExpand = async (uid) => {
    const isOpen = !!expanded[uid];
    if (isOpen) {
      setExpanded(prev => ({ ...prev, [uid]: null }));
      return;
    }

    setExpanded(prev => ({ ...prev, [uid]: { loading: true, items: [] } }));
    try {
      const { data } = await api.get(`/users/${uid}/documents`);
      setExpanded(prev => ({ ...prev, [uid]: { loading: false, items: data.documents || [] } }));
    } catch (e) {
      setExpanded(prev => ({ ...prev, [uid]: { loading: false, error: e.message, items: [] } }));
    }
  };

  const setUploadDraft = (typeCode, field, value) => {
    setUploadDrafts(prev => ({
      ...prev,
      [typeCode]: {
        ...(prev[typeCode] || {}),
        [field]: value
      }
    }));
  };

  const uploadMyDocument = async (document) => {
    const draft = uploadDrafts[document.type_code] || {};
    if (!draft.file || !draft.valid_to) {
      alert('Vyberte súbor a nastavte dátum platnosti.');
      return;
    }

    try {
      setUploadingCode(document.type_code);
      const formData = new FormData();
      formData.append('file', draft.file);
      formData.append('valid_to', draft.valid_to);
      await api.post(`/users/${user.id}/documents/${document.type_code}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadDrafts(prev => ({ ...prev, [document.type_code]: {} }));
      await loadMyDocuments();
      alert('Dokument bol nahratý.');
    } catch (e) {
      alert('Chyba pri nahrávaní: ' + (e.response?.data?.error || e.message || 'Neznáma chyba'));
    } finally {
      setUploadingCode(null);
    }
  };

  const mySummary = useMemo(() => ({
    total: myDocuments.length,
    ok: myDocuments.filter(d => d.status === 'ok').length,
    expiring: myDocuments.filter(d => d.status === 'expiring').length,
    expired: myDocuments.filter(d => d.status === 'expired').length,
    missing: myDocuments.filter(d => d.status === 'missing').length,
    optionalMissing: myDocuments.filter(d => d.status === 'optional_missing').length
  }), [myDocuments]);

  const filteredMyDocuments = useMemo(() => {
    if (statusFilter === 'all') return myDocuments;
    if (statusFilter === 'issues') return myDocuments.filter(d => ['missing', 'expired', 'expiring'].includes(d.status));
    return myDocuments.filter(d => d.status === statusFilter);
  }, [myDocuments, statusFilter]);

  const renderLegend = () => (
    <div className="legend">
      <span className="badge ok">Platný</span>
      <span className="badge expiring">Končí čoskoro</span>
      <span className="badge expired">Po splatnosti</span>
      <span className="badge missing">Chýba</span>
      <span className="badge optional_missing">Voliteľný – chýba</span>
    </div>
  );

  if (!canManageDocuments) {
    if (myLoading) return <div className="documents-container">Načítavam moje dokumenty...</div>;
    if (myError) return <div className="documents-container error">❌ {myError}</div>;

    return (
      <div className="documents-container my-documents-container">
        <div className="documents-header my-documents-header">
          <div>
            <h1 className="documents-title">📄 Moje dokumenty</h1>
            <p className="documents-subtitle">Prehľad nahraných, chýbajúcich a expirovaných dokumentov pre váš projekt.</p>
          </div>
          {renderLegend()}
        </div>

        <div className="my-documents-summary">
          <button type="button" className={`summary-tile ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
            <strong>{mySummary.total}</strong>
            <span>Všetky</span>
          </button>
          <button type="button" className={`summary-tile ok ${statusFilter === 'ok' ? 'active' : ''}`} onClick={() => setStatusFilter('ok')}>
            <strong>{mySummary.ok}</strong>
            <span>Platné</span>
          </button>
          <button type="button" className={`summary-tile warning ${statusFilter === 'issues' ? 'active' : ''}`} onClick={() => setStatusFilter('issues')}>
            <strong>{mySummary.missing + mySummary.expired + mySummary.expiring}</strong>
            <span>Na riešenie</span>
          </button>
          <button type="button" className={`summary-tile neutral ${statusFilter === 'optional_missing' ? 'active' : ''}`} onClick={() => setStatusFilter('optional_missing')}>
            <strong>{mySummary.optionalMissing}</strong>
            <span>Voliteľné chýbajú</span>
          </button>
        </div>

        <div className="my-documents-toolbar">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Všetky dokumenty</option>
            <option value="issues">Len na riešenie</option>
            <option value="missing">Chýbajúce</option>
            <option value="expired">Po splatnosti</option>
            <option value="expiring">Končí čoskoro</option>
            <option value="ok">Platné</option>
            <option value="optional_missing">Voliteľné – chýba</option>
          </select>
          <button type="button" onClick={loadMyDocuments}>Obnoviť</button>
        </div>

        <div className="my-documents-grid">
          {filteredMyDocuments.map((document) => {
            const draft = uploadDrafts[document.type_code] || {};
            const isUploading = uploadingCode === document.type_code;
            return (
              <article key={document.type_code} className={`my-document-card ${document.status}`}>
                <div className="my-document-card-header">
                  <div>
                    <h2>{displayDocName(document.name, document.type_code)}</h2>
                    {document.required ? <span className="required-label">Povinný dokument</span> : <span className="optional-label">Voliteľný dokument</span>}
                  </div>
                  <span className={`badge ${document.status}`}>{getStatusLabel(document.status)}</span>
                </div>

                {document.description && <p className="my-document-description">{document.description}</p>}

                <div className="my-document-meta">
                  <div>
                    <span>Platnosť do</span>
                    <strong className={document.status === 'expired' ? 'danger-text' : ''}>
                      {document.valid_to ? new Date(document.valid_to).toLocaleDateString('sk-SK') : 'Nie je nahrané'}
                    </strong>
                  </div>
                  <div>
                    <span>Nahrané</span>
                    <strong>{document.uploaded_at ? new Date(document.uploaded_at).toLocaleDateString('sk-SK') : '—'}</strong>
                  </div>
                  {document.days_remaining !== null && document.days_remaining !== undefined && (
                    <div>
                      <span>Zostáva</span>
                      <strong>{document.days_remaining} dní</strong>
                    </div>
                  )}
                </div>

                <div className="my-document-actions">
                  {document.download_url ? (
                    <button type="button" className="download-primary-btn" onClick={() => onDownload(document.download_url, document.name)}>
                      ⬇️ Stiahnuť aktuálny súbor
                    </button>
                  ) : (
                    <span className="missing-file-note">Súbor zatiaľ nie je nahraný.</span>
                  )}
                </div>

                <div className="my-document-upload">
                  <label>
                    Vyber súbor
                    <input
                      type="file"
                      accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => setUploadDraft(document.type_code, 'file', e.target.files?.[0] || null)}
                    />
                  </label>
                  <label>
                    Platné do
                    <input
                      type="date"
                      value={draft.valid_to || ''}
                      onChange={(e) => setUploadDraft(document.type_code, 'valid_to', e.target.value)}
                    />
                  </label>
                  <button type="button" onClick={() => uploadMyDocument(document)} disabled={isUploading}>
                    {isUploading ? 'Nahrávam...' : document.download_url ? 'Nahrať novú verziu' : 'Nahrať dokument'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {filteredMyDocuments.length === 0 && (
          <div className="documents-empty-state">Pre zvolený filter nie sú žiadne dokumenty.</div>
        )}
      </div>
    );
  }

  if (loading) return <div className="documents-container">Načítavam...</div>;
  if (error) return <div className="documents-container error">❌ {error}</div>;

  return (
    <div className="documents-container">
      <div className="documents-header">
        <h1 className="documents-title">📄 Dokumenty</h1>
        {renderLegend()}
      </div>
      <div className="users-list">
        {users.map(u => (
          <div key={u.id} className={`user-card ${u.blocked ? 'blocked' : ''}`}>
            <div className="user-header" onClick={() => toggleExpand(u.id)}>
              <span className="caret">{expanded[u.id] ? '▼' : '▶'}</span>
              <span className="user-name" style={{ color: u.blocked ? '#dc3545' : undefined }}>{u.full_name || u.username}</span>
            </div>
            {expanded[u.id] && (
              <div className="documents-panel">
                {expanded[u.id].loading ? (
                  <div className="loading">Načítavam dokumenty...</div>
                ) : expanded[u.id].error ? (
                  <div className="error">❌ {expanded[u.id].error}</div>
                ) : (
                  <table className="documents-table">
                    <thead>
                      <tr>
                        <th>Dokument</th>
                        <th>Stav</th>
                        <th>Platnosť do</th>
                        <th style={{ textAlign: 'right' }}>Akcie</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expanded[u.id].items.map((d) => (
                        <tr key={d.type_code}>
                          <td>{displayDocName(d.name, d.type_code)}</td>
                          <td><span className={`badge ${d.status}`}>{getStatusLabel(d.status)}</span></td>
                          <td style={{ color: d.status === 'expired' ? '#dc3545' : undefined }}>
                            {d.valid_to ? new Date(d.valid_to).toLocaleDateString('sk-SK') : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {d.download_url ? (
                              <button
                                className="download-btn"
                                onClick={() => onDownload(d.download_url, d.name)}
                                title={`Stiahnuť: ${d.name}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              >
                                <span aria-hidden>⬇️</span>
                                <span>Stiahnuť</span>
                              </button>
                            ) : (
                              <span style={{ color: '#6c757d' }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Documents;
